/**
 * Сборка адреса одной строкой из составных полей.
 *
 * Формы приказа МЭР № 530 требуют адрес по частям (субъект, район, город,
 * улица, дом), а заявление печатает его одной строкой. Заполнять и то и другое
 * вручную — двойная работа, поэтому строка собирается из частей.
 *
 * Правка строки вручную имеет приоритет: юрист может привести её к виду,
 * который стоит в паспорте, и эта правка не теряется (признак `fullManual`).
 */

import type { Address } from '../types/common.js';

/** Порядок частей — как в адресе на конверте. */
const PARTS: Array<keyof Address> = [
  'postalCode',
  'region',
  'area',
  'city',
  'settlement',
  'street',
  'house',
  'block',
  'flat',
];

export function composeAddress(address: Address): string {
  return PARTS.map((key) => (address[key] as string | undefined)?.trim())
    .filter((value): value is string => Boolean(value))
    .join(', ');
}

/**
 * Изменение составной части адреса с пересборкой строки.
 * Строка не трогается, если её правили вручную.
 */
export function updateAddressPart(address: Address, key: keyof Address, value: string): Address {
  const next: Address = { ...address, [key]: value, source: 'manual' };
  if (next.fullManual) return next;
  return { ...next, full: composeAddress(next) };
}

/** Ручная правка строки адреса: дальнейшая пересборка отключается. */
export function setAddressFull(address: Address, full: string): Address {
  return { ...address, full, fullManual: true, source: 'manual' };
}

/** Адрес заполнен настолько, что его можно печатать в документе. */
export function isAddressUsable(address: Address): boolean {
  return Boolean(address.full?.trim()) || Boolean(address.region?.trim() && address.house?.trim());
}
